<template>
  <Teleport to="body">
    <Transition name="fade" appear>
      <div class="backdrop" @mousedown.self="close" role="presentation">
        <Transition name="pop" appear>
          <div
            class="dialog"
            :class="`dialog-${size}`"
            role="dialog"
            aria-modal="true"
            :aria-label="title"
          >
            <header v-if="title || $slots.header">
              <slot name="header">
                <div>
                  <h2>{{ title }}</h2>
                  <p v-if="subtitle">{{ subtitle }}</p>
                </div>
              </slot>
              <button class="icon-btn" @click="close" aria-label="Close">
                <i class="ri-close-line"></i>
              </button>
            </header>
            <div class="body scroll-thin">
              <slot />
            </div>
            <footer v-if="$slots.footer">
              <slot name="footer" />
            </footer>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
// A dialog over the page: Escape or a click outside closes it, and the page
// behind does not scroll while it is open.
export default {
  name: "Modal",
  emits: ["close"],
  props: {
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    size: { type: String, default: "md" }, // sm, md, lg
  },
  mounted() {
    document.addEventListener("keydown", this.onKey);
    document.body.style.overflow = "hidden";
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKey);
    document.body.style.overflow = "";
  },
  methods: {
    close() {
      this.$emit("close");
    },
    onKey(e) {
      if (e.key === "Escape") this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(20, 17, 26, 0.45);
  backdrop-filter: blur(4px);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: min(88vh, 860px);
  background: var(--surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;

  &.dialog-sm {
    max-width: 420px;
  }
  &.dialog-md {
    max-width: 560px;
  }
  &.dialog-lg {
    max-width: 720px;
  }

  @media (max-width: 560px) {
    align-self: flex-end;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    max-height: 92vh;
  }
}

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.4rem 1.5rem 0.6rem;

  h2 {
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  p {
    margin-top: 0.25rem;
    font-size: 0.88rem;
    color: var(--ink-3);
  }
}

.body {
  padding: 0.8rem 1.5rem 1.4rem;
  overflow-y: auto;
}

footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--line);
  background: var(--canvas);
}
</style>
